import React, { useState } from "react";
import { useNavigate, useLocation, Outlet, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  BookOpen, Users, CalendarClock, SlidersHorizontal, BarChart3, ScrollText, Inbox, Youtube,
  ArrowLeft, PanelLeftClose, PanelLeftOpen, School, TrendingUp,
} from "lucide-react";

// Shell structure ported from Quran Fluency's admin (ADMIN_DESIGN_SYSTEM.md
// "Shell"/"Nav") — fixed-width sidebar + fluid content column, active item
// gets a left accent bar — restyled with 600 Stories' own hv-* tokens.
// Deliberate deviation from source: source only docks the sidebar at `lg:`
// and stacks it above the content below that; ours stays docked left at
// every width (per explicit user request), since this is a desktop admin
// tool where a top-stacked nav was actively confusing.
const SHARED_NAV = [
  { to: "/admin/stories", label: "Stories", icon: BookOpen, match: (p) => p.startsWith("/admin/stories") },
  { to: "/admin/inbox", label: "Inbox", icon: Inbox },
];

const ADMIN_ONLY_NAV = [
  { to: "/admin/schools", label: "Schools", icon: School },
  { to: "/admin/school-progress", label: "School Progress", icon: TrendingUp },
  { to: "/admin/users", label: "Users", icon: Users },
  { to: "/admin/release", label: "Weekly Release", icon: CalendarClock },
  { to: "/admin/weekly-recaps", label: "Weekly Recaps", icon: Youtube },
  { to: "/admin/overrides", label: "Learner Overrides", icon: SlidersHorizontal },
  { to: "/admin/analytics", label: "Analytics", icon: BarChart3 },
  { to: "/admin/audit-log", label: "Audit Log", icon: ScrollText },
];

export function RequireAdminOnly({ children }) {
  const { user } = useAuth();
  if (!user) return null;
  if (user.role !== "admin") return <Navigate to="/admin/stories" replace />;
  return children;
}

export default function AdminShell() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Auto-collapse below lg (1024px): the sidebar never responsively hides
  // itself otherwise, so on a phone it renders at its full w-56 (224px) and
  // leaves every admin page only ~110px of actual content width — the root
  // cause behind columns disappearing from tables, labels truncating, and
  // buttons overflowing across the whole admin panel on mobile. Starting
  // collapsed (icon-only, ~64-80px) on narrow screens fixes all of that at
  // once; the manual toggle still works either direction from there.
  const [collapsed, setCollapsed] = useState(() => typeof window !== "undefined" && window.innerWidth < 1024);

  if (!user) return <div className="p-12 text-hv-slate">Loading…</div>;
  const isAdmin = user.role === "admin";
  const isTeacher = user.role === "teacher";
  if (!isAdmin && !isTeacher) return <Navigate to="/dashboard" replace />;

  const nav = isAdmin ? [...SHARED_NAV, ...ADMIN_ONLY_NAV] : SHARED_NAV;

  return (
    <div className="bg-hv-cream min-h-[calc(100vh-73px)]" data-testid="admin-shell">
      {/* Fluid, not max-w-7xl-capped — per explicit user request the admin
          panel should stretch to fill the browser like the Little Verses
          reference (poems.hikmahverses.com/admin), not sit centered with
          empty cream margins on a wide screen. */}
      <div className="w-full px-4 sm:px-6 lg:px-8 py-6 lg:py-8 flex flex-row items-start gap-6">
        <aside className={`shrink-0 sticky top-8 self-start transition-all duration-200 ${collapsed ? "w-16 sm:w-20" : "w-56 sm:w-60"}`}>
          <div className="hv-card p-4">
            <div className="flex items-center justify-between px-1 sm:px-3">
              {!collapsed && (
                <span className="hv-heading uppercase tracking-[0.15em] text-xs text-hv-coral">Admin</span>
              )}
              <button
                type="button"
                onClick={() => setCollapsed((c) => !c)}
                data-testid="admin-sidebar-collapse"
                className="inline-flex text-hv-slate hover:bg-hv-cream hover:text-hv-navy rounded-xl p-1.5 ms-auto"
              >
                {collapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
              </button>
            </div>

            <button
              type="button"
              onClick={() => navigate("/dashboard")}
              data-testid="admin-back-to-dashboard"
              className={`w-full flex items-center gap-3 rounded-2xl px-3 py-2.5 mt-2 mb-3 text-sm hv-heading font-semibold text-hv-slate hover:bg-hv-cream hover:text-hv-navy transition-colors duration-200 ${collapsed ? "justify-center" : ""}`}
            >
              <ArrowLeft size={16} strokeWidth={2.4} />
              {!collapsed && <span>Back to dashboard</span>}
            </button>
            <div className="h-px bg-[#E5DFC8] mb-3" />

            <nav className="space-y-1" data-testid="admin-nav">
              {nav.map((item) => {
                const active = item.match ? item.match(location.pathname) : location.pathname.startsWith(item.to);
                const Icon = item.icon;
                return (
                  <button
                    key={item.to}
                    type="button"
                    onClick={() => navigate(item.to)}
                    data-testid={`admin-nav-${item.to.split("/").pop()}`}
                    title={collapsed ? item.label : undefined}
                    className={`relative w-full flex items-center gap-3 rounded-2xl px-3 py-2.5 text-sm hv-heading font-semibold transition-all duration-200 text-left group ${collapsed ? "justify-center" : ""} ${
                      active ? "bg-hv-tealTint text-hv-navy" : "text-hv-slate hover:bg-hv-cream hover:text-hv-navy hover:translate-x-0.5"
                    }`}
                  >
                    {active && <span className="absolute left-0 top-1/2 -translate-y-1/2 h-5 w-1 rounded-full bg-hv-coral" aria-hidden="true" />}
                    <Icon className={`w-4 h-4 shrink-0 transition-transform duration-200 group-hover:scale-110 ${active ? "ml-1" : ""}`} strokeWidth={2.4} />
                    {!collapsed && <span>{item.label}</span>}
                  </button>
                );
              })}
            </nav>
          </div>
        </aside>

        <main className="flex-1 min-w-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
